// components/LoginForm.tsx
import { Button } from "@/components/ui/button";
import { HausCheckbox } from "@/components/ui/hauscheckbox";
import { Input } from "@/components/ui/input";
import { Separator } from "@radix-ui/react-select";
import { Form, Link } from "@remix-run/react";
import { AlertCircle } from "lucide-react";
import { useEffect, useRef } from "react";

type ActionData = {
    errors?: {
        email?: string;
        password?: string;
        [key: string]: string | undefined;
    };
};

type Props = {
    actionData: ActionData | undefined;
    redirectTo: string;
    isLoading: boolean;
    clientid: string;
};

export function LoginForm({ actionData, redirectTo, isLoading, clientid }: Props) {
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (actionData?.errors?.email) {
            emailRef.current?.focus();
        } else if (actionData?.errors?.password) {
            passwordRef.current?.focus();
        }
    }, [actionData]);

    return (
        <div className="py-6 w-full max-w-xl">
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="loader" />
                </div>
            )}
            <Form method="post" className="grid gap-4" noValidate>
                <div>
                    <Input
                        ref={emailRef}
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoFocus
                        autoComplete="email"
                        placeholder="Username"
                        className="text-white bg-black rounded-full border border-gray-300 focus:border-hausorange focus:ring-hausorange font-thin tracking-wide p-5"
                        aria-invalid={!!actionData?.errors?.email}
                    />
                    {actionData?.errors?.email && (
                        <div className="flex items-center space-x-2 mt-3 p-3 bg-red-500/10 border border-red-200/40 rounded-lg backdrop-blur-lg animate-slide-down">
                            <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                            <p className="text-sm text-red-200 font-thin">{actionData.errors.email}</p>
                        </div>
                    )}
                </div>

                <div>
                    <Input
                        ref={passwordRef}
                        id="password"
                        name="password"
                        type="password"
                        required
                        autoComplete="current-password"
                        placeholder="Password"
                        className="text-white bg-black rounded-full border border-gray-300 focus:border-hausorange focus:ring-hausorange font-thin tracking-wide p-5"
                        aria-invalid={!!actionData?.errors?.password}
                    />
                    {actionData?.errors?.password && (
                        <div className="flex items-center space-x-2 mt-3 p-3 bg-red-500/10 border border-red-200/40 rounded-lg backdrop-blur-lg animate-slide-down">
                            <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                            <p className="text-sm text-red-200 font-thin">{actionData.errors.password}</p>
                        </div>
                    )}
                </div>

                <input type="hidden" name="redirectTo" value={redirectTo} />

                <div className="flex items-center justify-center sm:justify-start space-x-4">
                    <Button type="submit" className="bg-white text-black rounded-full uppercase hover:bg-hausorange hover:*:text-white transition-colors duration-300 ease-in-out font-semibold tracking-wide px-6 py-2" disabled={isLoading}>
                        Login
                    </Button>
                    <div className="flex items-center space-x-2">
                        <label htmlFor="remember" className="text-white text-sm font-thin">Remember me?</label>
                        <HausCheckbox id="remember" name="remember" className="border !border-white" />
                    </div>
                </div>
                <div className="flex items-center justify-center space-x-4 sm:mt-4 text-white/70 sm:text-sm  text-xs">

                    <Link to="/recover" className="text-xs text-white/70 sm:text-sm  underline font-thin">
                        Forgot password?
                    </Link>

                    {clientid === "amandaai" && (
                        <>
                            <Separator className="w-px h-6 bg-hausorange" />
                            <p className="text-xs text-white/70 sm:text-sm  ">
                                Don&apos;t have an account?{' '}
                                <Link to="/join" className="underline text-hausorange">
                                    Sign up!
                                </Link>
                            </p>
                        </>
                    )}
                </div>
            </Form>
        </div>
    );
}
export default LoginForm;